import React, { useMemo, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { Form as CustomForm } from '../../../../../../../../components/form/Form';
import { ControlledDateInput } from '../../../../../../../../components/form/Datepicker';
import { FormGroup } from '../../../../../../../../components/form/FormGroup';
import InputLabel from '../../../../../../../../components/form/InputLabel';
import { validateDateRange } from '../utils/dateValidation';
import { toDateOnlyString } from '../../../../../../../../utils/dateUtils';
import { t as rootT } from 'i18next';

const EffectiveDateWatcher: React.FC<{
  onEffectiveDateChange: (value: string | null) => void;
}> = ({ onEffectiveDateChange }) => {
  const { watch } = useFormContext();
  const watchedEffectiveDate = watch('effectiveDate');

  useEffect(() => {
    const dateValue = toDateOnlyString(watchedEffectiveDate);
    onEffectiveDateChange(dateValue);
  }, [watchedEffectiveDate, onEffectiveDateChange]);

  return null;
};

const EndDateWatcher: React.FC<{
  onEndDateChange: (value: string | null) => void;
}> = ({ onEndDateChange }) => {
  const { watch } = useFormContext();
  const watchedEndDate = watch('endDate');

  useEffect(() => {
    const dateValue = toDateOnlyString(watchedEndDate);
    onEndDateChange(dateValue);
  }, [watchedEndDate, onEndDateChange]);

  return null;
};

export const DateControls: React.FC<{
  effectiveDate: string;
  unknownEndDate: boolean;
  endDate: string;
  onEffectiveDateChange: (value: string | null) => void;
  onEndDateChange: (value: string | null) => void;
  onUnknownEndDateChange: (value: boolean) => void;
  t: (key: string) => string;
}> = ({
  effectiveDate,
  unknownEndDate,
  endDate,
  onEffectiveDateChange,
  onEndDateChange,
  onUnknownEndDateChange,
  t,
}) => {
  const validationErrors = useMemo(() => {
    return validateDateRange(
      effectiveDate,
      unknownEndDate ? undefined : endDate,
    );
  }, [effectiveDate, endDate, unknownEndDate]);

  return (
    <div>
      <CustomForm
        key="date-controls-form"
        onSubmit={() => {}}
        defaultValues={{
          effectiveDate: effectiveDate,
          endDate: endDate,
        }}>
        <>
          <EffectiveDateWatcher onEffectiveDateChange={onEffectiveDateChange} />
          <EndDateWatcher onEndDateChange={onEndDateChange} />
          <FormGroup>
            <InputLabel label={t('effectiveDate')} required={true} />
            <ControlledDateInput
              name="effectiveDate"
              type="date"
              options={{
                dateFormat: 'Y-m-d',
                altInput: true,
                altFormat: rootT('flatpickr.dateFormat'),
              }}
            />
            {validationErrors.effectiveDateError && (
              <Form.Control.Feedback
                type="invalid"
                style={{ display: 'block' }}>
                {validationErrors.effectiveDateError}
              </Form.Control.Feedback>
            )}
          </FormGroup>

          <Form.Check
            type="switch"
            id="unknown-end-date"
            label={t('noEndDate')}
            checked={unknownEndDate}
            onChange={(e) => onUnknownEndDateChange(e.target.checked)}
            className="mb-3"
          />

          {!unknownEndDate && (
            <FormGroup>
              <InputLabel label={t('endDate')} required={false} />
              <ControlledDateInput
                name="endDate"
                type="date"
                options={{
                  dateFormat: 'Y-m-d',
                  altInput: true,
                  altFormat: rootT('flatpickr.dateFormat'),
                }}
              />
              {validationErrors.endDateError && (
                <Form.Control.Feedback
                  type="invalid"
                  style={{ display: 'block' }}>
                  {validationErrors.endDateError}
                </Form.Control.Feedback>
              )}
            </FormGroup>
          )}
        </>
      </CustomForm>
    </div>
  );
};
